'use client';

import * as React from 'react';
import { DatePickerInput } from '@mantine/dates';
import { useTheme } from '@/contexts/ThemeContext';

interface DatePickerProps {
  value?: Date | undefined;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  highlightedDates?: Date[];
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  fromDate,
  toDate,
  highlightedDates = []
}: DatePickerProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // Ensure value is a valid Date or null
  const normalizedValue = React.useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [value]);

  const handleChange = (date: Date | null) => {
    if (date) {
      // Ensure date is a proper Date object
      const dateObj = date instanceof Date ? date : new Date(date);

      // Validate the date
      if (isNaN(dateObj.getTime())) {
        onChange?.(undefined);
        return;
      }

      // Mantine sometimes gives us dates with timezone offset issues
      // Extract UTC date components (what the user actually selected)
      const year = dateObj.getUTCFullYear();
      const month = dateObj.getUTCMonth();
      const day = dateObj.getUTCDate();

      // Create a new date at noon in local timezone using the UTC components
      const correctedDate = new Date(year, month, day, 12, 0, 0, 0);

      onChange?.(correctedDate);
    } else {
      onChange?.(undefined);
    }
  };

  // Disable dates outside range
  const excludeDate = (date: Date) => {
    // Disable dates before fromDate
    if (fromDate && date < fromDate) return true;

    // Disable dates after toDate
    if (toDate && date > toDate) return true;

    return false;
  };

  // Create display value that shows the correct local date
  const displayValue = React.useMemo(() => {
    if (!normalizedValue) return null;

    // Create a new date at start of day in local timezone
    const year = normalizedValue.getFullYear();
    const month = normalizedValue.getMonth();
    const day = normalizedValue.getDate();

    // Create date at midnight local time
    return new Date(year, month, day, 0, 0, 0, 0);
  }, [normalizedValue]);

  // Create a Set of highlighted date strings for efficient lookup
  const highlightedDateStrings = React.useMemo(() => {
    return new Set(
      highlightedDates
        .filter(d => d && typeof d.getFullYear === 'function')
        .map(d => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`)
    );
  }, [highlightedDates]);

  // Check if a date should be highlighted
  const isHighlighted = (date: Date): boolean => {
    // Safety check - ensure date is a valid Date object
    if (!date || typeof date.getFullYear !== 'function') {
      return false;
    }
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return highlightedDateStrings.has(key);
  };

  // Get day props for highlighting dates with assignments (circle outline)
  const getDayProps = (date: Date) => {
    // Safety check for valid date
    if (!date || typeof date.getFullYear !== 'function') {
      return {};
    }
    if (isHighlighted(date)) {
      return {
        style: {
          border: isDark ? '2px solid hsl(142.1 76.2% 46.3%)' : '2px solid hsl(142.1 76.2% 36.3%)',
          borderRadius: '50%',
          fontWeight: 600
        }
      };
    }
    return {};
  };

  return (
    <DatePickerInput
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      excludeDate={excludeDate}
      valueFormat='ddd, MMM D, YYYY'
      minDate={fromDate}
      maxDate={toDate}
      getDayProps={getDayProps}
      firstDayOfWeek={0}
      dropdownType='modal'
      modalProps={{
        zIndex: 10000
      }}
      styles={{
        input: {
          height: '40px',
          fontSize: '14px',
          borderRadius: '6px',
          borderColor: 'hsl(var(--input))',
          backgroundColor: 'hsl(var(--background))',
          color: 'hsl(var(--foreground))'
        },
        day: {
          height: '44px',
          width: '44px',
          fontSize: '14px'
        },
        calendar: {
          backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
          borderColor: 'hsl(var(--border))'
        },
        calendarHeader: {
          backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))'
        },
        calendarHeaderControl: {
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
          '&:hover': {
            backgroundColor: isDark ? 'hsl(var(--accent))' : 'hsl(var(--accent))'
          }
        },
        calendarHeaderLevel: {
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
          '&:hover': {
            backgroundColor: isDark ? 'hsl(var(--accent))' : 'hsl(var(--accent))'
          }
        },
        weekday: {
          color: isDark ? 'hsl(var(--muted-foreground))' : 'hsl(var(--muted-foreground))'
        },
        month: {
          backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))'
        },
        year: {
          backgroundColor: isDark ? 'hsl(var(--popover))' : 'hsl(var(--background))',
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))'
        },
        monthCell: {
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
          '&:hover': {
            backgroundColor: isDark ? 'hsl(var(--accent))' : 'hsl(var(--accent))'
          }
        },
        yearCell: {
          color: isDark ? 'hsl(var(--popover-foreground))' : 'hsl(var(--foreground))',
          '&:hover': {
            backgroundColor: isDark ? 'hsl(var(--accent))' : 'hsl(var(--accent))'
          }
        }
      }}
    />
  );
}
